import { type FormEvent, useRef, useState } from "react";
import type * as api from "./api";
import { type CheckAnswer, checkText, finishProblem, parseOdometer, SAME_MILES, startProblem } from "./pretrip";

/** Keeps the entry on the phone; a throw gives the text to show. */
type Save<T> = (body: T) => Promise<void>;
const problemOf = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Rule D-11: the pre-trip check and the start odometer, before the first stop. */
export function RouteStartForm({
  route,
  items,
  save,
}: {
  route: api.Route;
  items: readonly { id: string; label: string }[];
  save: Save<api.RouteStart>;
}) {
  const [answers, setAnswers] = useState<Record<string, CheckAnswer>>({});
  const [odometer, setOdometer] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const last = route.last_odometer ?? null;
  const answer = (id: string, fields: Partial<CheckAnswer>) => {
    setAnswers((all) => ({ ...all, [id]: { ...(all[id] ?? { ok: null, note: "" }), ...fields } }));
    setProblem(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const wrong = startProblem(items, answers, odometer, last);
    if (wrong) {
      setProblem(wrong);
      const open = items.find((item) => answers[item.id]?.ok == null);
      const bare = items.find((item) => answers[item.id]?.ok === false && !answers[item.id].note.trim());
      const target = open
        ? `input[name="check-${route.id}-${open.id}"]`
        : bare
          ? `[data-note-for="${bare.id}"]`
          : '[data-testid="pretrip-odometer"]';
      formRef.current?.querySelector<HTMLInputElement>(target)?.focus();
      return;
    }
    const miles = parseOdometer(odometer);
    if (miles === null) return;
    setSaving(true);
    try {
      const results = items.map((item) => ({ item_id: item.id, ok: answers[item.id]?.ok === true, note: answers[item.id]?.note.trim() ?? "" }));
      await save({ odometer: miles, results });
    } catch (e) {
      setProblem(problemOf(e));
    } finally {
      setSaving(false);
    }
  };
  const gated = items.length > 0;
  return (
    <form ref={formRef} className="route-check" data-testid="pretrip-form" onSubmit={submit} noValidate>
      <fieldset disabled={saving}>
        <legend>{gated ? "Pre-trip check" : "Start the route"}</legend>
        {gated && <p className="hint">Check each item before the first stop. Tell what is wrong with each item that is not OK.</p>}
        {items.map((item) => {
          const given = answers[item.id];
          return (
            <div key={item.id} className="check-item" data-testid="pretrip-item" role="group" aria-labelledby={`check-${route.id}-${item.id}`}>
              <span id={`check-${route.id}-${item.id}`} className="check-label">
                {item.label}
              </span>
              <label>
                <input type="radio" name={`check-${route.id}-${item.id}`} data-testid="pretrip-ok" checked={given?.ok === true} onChange={() => answer(item.id, { ok: true })} />
                OK
              </label>
              <label>
                <input type="radio" name={`check-${route.id}-${item.id}`} data-testid="pretrip-bad" checked={given?.ok === false} onChange={() => answer(item.id, { ok: false })} />
                Not OK
              </label>
              {given?.ok === false && (
                <label className="check-note">
                  What is wrong
                  <input data-testid="pretrip-note" data-note-for={item.id} value={given.note} maxLength={500} onChange={(e) => answer(item.id, { note: e.target.value })} />
                </label>
              )}
            </div>
          );
        })}
        <label className="odometer">
          Odometer (miles)
          <input
            data-testid="pretrip-odometer"
            inputMode="numeric"
            autoComplete="off"
            value={odometer}
            placeholder={last === null ? undefined : String(last)}
            onChange={(e) => {
              setOdometer(e.target.value);
              setProblem(null);
            }}
          />
        </label>
        {last !== null && <p className="hint">Last reading of truck {route.truck}: {last.toLocaleString("en-US")} miles.</p>}
        {problem && (
          <p role="alert" className="error" data-testid="pretrip-problem">
            {problem}
          </p>
        )}
        <div className="actions">
          <button className="primary" data-testid="pretrip-start">
            {saving ? "Starting…" : "Start route"}
          </button>
        </div>
      </fieldset>
    </form>
  );
}

/** Spec F-RTE-12: the end odometer after the last stop. */
export function RouteFinishForm({ route, save }: { route: api.Route; save: Save<number> }) {
  const [odometer, setOdometer] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sameAsked, setSameAsked] = useState(false);
  const start = route.odometer_start ?? null;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const wrong = finishProblem(start, odometer);
    const miles = parseOdometer(odometer);
    if (wrong || miles === null) {
      setProblem(wrong ?? "Enter the odometer in whole miles.");
      return;
    }
    if (miles === start && !sameAsked) {
      setSameAsked(true);
      setProblem(SAME_MILES);
      return;
    }
    setSaving(true);
    try {
      await save(miles);
    } catch (e) {
      setProblem(problemOf(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form className="route-check" data-testid="finish-form" data-start={start ?? ""} onSubmit={submit} noValidate>
      <fieldset disabled={saving}>
        <legend>Finish the route</legend>
        <label className="odometer">
          End odometer (miles)
          <input
            data-testid="finish-odometer"
            inputMode="numeric"
            autoComplete="off"
            value={odometer}
            onChange={(e) => {
              setOdometer(e.target.value);
              setSameAsked(false);
              setProblem(null);
            }}
          />
        </label>
        {start !== null && <p className="hint">Start: {start.toLocaleString("en-US")} miles.</p>}
        {problem && (
          <p role="alert" className="error" data-testid="finish-problem">
            {problem}
          </p>
        )}
        <div className="actions">
          <button className="primary" data-testid="finish-save">
            {saving ? "Saving…" : sameAsked ? "Finish route with 0 miles" : "Finish route"}
          </button>
        </div>
      </fieldset>
    </form>
  );
}

/** The check of a route in one line, with the items that are not OK. */
export function RouteCheckSummary({ route }: { route: api.Route }) {
  const check = route.check ?? null;
  if (!check && route.odometer_start == null) return null;
  const bad = check ? check.results.filter((result) => !result.ok) : [];
  return (
    <div className="route-check-summary" data-testid="route-check" data-failed={bad.length}>
      <p className="sub">{check ? checkText(check, route.odometer_end ?? null) : `Start ${route.odometer_start} mi`}</p>
      {bad.length > 0 && (
        <ul className="check-failed">
          {bad.map((result) => (
            <li key={result.item_id}>
              <span className="chip-state skipped">Not OK</span> {result.label}: {result.note}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
